import React, { forwardRef, useImperativeHandle, useState } from 'react';
import DatePicker from 'react-datepicker';
import EventIcon from '@mui/icons-material/Event';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import DatePickerWrapper from '@stack/theme/styles/libs/datepicker';
import format from 'date-fns/format';

import { DatePickerRangeProps, PickerProps, RefProps } from './props';

export const DatePickerRange = forwardRef<RefProps, DatePickerRangeProps>(
  (props, ref) => {
    const { onChange, popperPlacement } = props;
    // ** States
    const [startDate, setStartDate] = useState<Date | null | undefined>(null);
    const [endDate, setEndDate] = useState<Date | null | undefined>(null);

    const handleOnChange = (dates: any) => {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);
      onChange({
        startDate: start ? format(start, 'MM/dd/yyyy') : undefined,
        endDate: end ? format(end, 'MM/dd/yyyy') : undefined,
      });
    };

    useImperativeHandle<RefProps, any>(ref, () => ({
      handleReset: () => {
        setStartDate(null);
        setEndDate(null);
        onChange({
          startDate: undefined,
          endDate: undefined,
        });
      },
    }));

    const CustomInput = forwardRef((props: PickerProps, ref) => {
      const { start, end, label } = props;
      const startDate = start !== null ? format(start, 'MM/dd/yyyy') : null;
      const endDate = end !== null ? format(end, 'MM/dd/yyyy') : null;
      let value;
      if (startDate) {
        value = startDate + ' - ' + (endDate !== null ? endDate : '');
      }

      return (
        <TextField
          {...props}
          inputRef={ref}
          fullWidth
          label={label || ''}
          value={value}
          placeholder={'Start Date - End Date'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end">
                  <EventIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onFocus={(
            e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
          ) => e.target.blur()}
        />
      );
    });

    return (
      <DatePickerWrapper>
        <DatePicker
          selectsRange
          endDate={endDate}
          selected={startDate}
          startDate={startDate}
          id="date-range-picker"
          onChange={handleOnChange}
          shouldCloseOnSelect={false}
          popperPlacement={popperPlacement}
          customInput={
            <CustomInput
              label="Date Range"
              start={startDate as Date | number}
              end={endDate as Date | number}
            />
          }
        />
      </DatePickerWrapper>
    );
  }
);

export * from './props';
export default DatePickerRange;
